import React, { useMemo } from 'react';
import useSWR from 'swr';
import { Flex, Tag, TagLabel } from '@chakra-ui/react';
import { fetchData } from '../../services/fetch';

/**
 * GRI-02 — os indicadores vinculados à questão, como tags.
 *
 * ACEITA AS DUAS FORMAS, e isso não é conveniência:
 *
 *   `gri`     — objetos prontos (código, nome), como vêm no payload das telas
 *               de questionário. Nada é buscado.
 *   `griIds`  — só os ids, que é o que o FORMULÁRIO do template guarda. Aqui os
 *               códigos são resolvidos contra o catálogo.
 *
 * Por que o formulário guarda id e não o objeto: ao trocar os indicadores no
 * modal, o que muda é a lista de ids. Se o card lesse uma cópia dos objetos,
 * ela ficaria velha no instante seguinte à edição e o card mostraria o código
 * ANTIGO até alguém recarregar a página. Com o id como fonte única, o código
 * exibido acompanha a edição.
 *
 * A busca usa a MESMA chave de SWR do GriMultiSelect, que já roda nessa tela:
 * o SWR deduplica, então isto não acrescenta requisição nenhuma.
 *
 * Cores explícitas, sem colorScheme: o tema do projeto sobrescreve os tons 100
 * com a cor de marca, e o variant='subtle' usaria o tom 100 como FUNDO.
 */
const RotuloGri = ({ gri, griIds, max = 4, comRotulo = false }) => {
  const precisaResolver = !gri?.length && !!griIds?.length;
  const { data: catalogo } = useSWR(
    precisaResolver ? '/gri-indicator' : null,
    fetchData,
  );

  const indicadores = useMemo(() => {
    if (gri?.length) return gri;
    if (!griIds?.length) return [];

    // Enquanto o catálogo não chega, mostra o id — o selo aparece na hora certa
    // e só o texto fica provisório. Sumir e reaparecer piscaria a lista inteira.
    return griIds.map(
      (id) =>
        (catalogo || []).find((item) => item.id_gri_indicator === id) || {
          id_gri_indicator: id,
          code: `#${id}`,
        },
    );
  }, [gri, griIds, catalogo]);

  if (!indicadores.length) return null;

  const visiveis = indicadores.slice(0, max);
  const restantes = indicadores.length - visiveis.length;

  return (
    <Flex gap='4px' wrap='wrap' mt='4px' align='center'>
      {/* O rótulo por extenso: o código sozinho não diz, a quem não convive com
          a norma, que aquela pergunta ALIMENTA um indicador que vai para o
          gráfico e para a comparação de mercado. */}
      {comRotulo && (
        <Tag size='sm' bg='#E7F6EC' color='#1B5E30' borderRadius='6px'>
          <TagLabel fontSize='11px' fontWeight={700} letterSpacing='0.2px'>
            INDICADOR GRI
          </TagLabel>
        </Tag>
      )}

      {visiveis.map((indicador) => (
        <Tag
          key={indicador.id_gri_indicator}
          size='sm'
          bg='#EAF7EE'
          color='#1B5E30'
          borderRadius='6px'
          // GRI-07: a unidade entra no tooltip (não no rótulo, que já é
          // apertado) — quem classifica precisa saber que tipo de medida
          // aquele campo vai pedir na tela do respondente.
          title={indicador.unit ? `${indicador.name} (${indicador.unit})` : indicador.name}
        >
          <TagLabel fontSize='12px'>{indicador.code}</TagLabel>
        </Tag>
      ))}

      {/* Questão com muitos indicadores não pode empurrar o resto do card para
          fora da tela; o título da tag guarda o que ficou de fora. */}
      {restantes > 0 && (
        <Tag
          size='sm'
          bg='#F0F0F0'
          color='#666666'
          borderRadius='6px'
          title={indicadores
            .slice(max)
            .map((indicador) => indicador.code)
            .join(', ')}
        >
          <TagLabel fontSize='12px'>+{restantes}</TagLabel>
        </Tag>
      )}
    </Flex>
  );
};

export default RotuloGri;
